import { mayControlImpersonation } from './decision';
import { createImpersonationStore } from './store';
import { persistStickyImpersonateEmail, resolveRequestedImpersonateEmail, writeImpersonateToBrowserUrl, } from './urlSync';
let singleton = null;
/**
 * Wires the impersonation store to the auth module (ROWILABDEV-72 semantics):
 *  - Impersonation is applied at the exchange (API root query), so while it is active the
 *    exchanged `subject` describes the impersonated user — never a superuser. The store is
 *    therefore kept when the user is a superuser OR when the exchange honored the
 *    requested impersonation (subject email == requested email, which only happens for
 *    Rowi admins); it is cleared for everyone else.
 *  - Changing the impersonation email re-runs the auth pipeline so `subject` and the root
 *    potentialActions reflect the new principal. (When data hooks appear, invalidate their
 *    query caches on this transition too.)
 *
 * Idempotent. Returns the existing module if already installed.
 */
export function installImpersonation(auth) {
    if (singleton)
        return singleton;
    const store = createImpersonationStore();
    const clearState = () => {
        persistStickyImpersonateEmail('');
        writeImpersonateToBrowserUrl('');
        store.setEmail('');
    };
    const onAuthChange = () => {
        const status = auth.getStatus();
        if (status.kind !== 'authenticated') {
            // Stale impersonation state can be the very reason we are blocked (the API
            // rejects impersonation it won't honor). Scrub it and re-run the pipeline once
            // so a leftover value can never lock the user out.
            if (status.kind === 'blocked' && resolveRequestedImpersonateEmail()) {
                clearState();
                void auth.refreshStatus();
                return;
            }
            if (store.getEmail())
                clearState();
            return;
        }
        const requested = resolveRequestedImpersonateEmail();
        if (!mayControlImpersonation(status.user, requested)) {
            if (store.getEmail() || requested)
                clearState();
            return;
        }
        if (requested) {
            persistStickyImpersonateEmail(requested);
            writeImpersonateToBrowserUrl(requested);
            store.setEmail(requested);
        }
    };
    onAuthChange();
    const unsubscribe = auth.subscribe(onAuthChange);
    const module = {
        getEmail: () => store.getEmail(),
        subscribe: (listener) => store.subscribe(listener),
        setEmail(next) {
            persistStickyImpersonateEmail(next);
            writeImpersonateToBrowserUrl(next);
            store.setEmail(next);
            // Re-run the auth pipeline so the exchange picks the new (or cleared) principal up.
            void auth.refreshStatus();
        },
        dispose() {
            unsubscribe();
            if (singleton === module)
                singleton = null;
        },
    };
    singleton = module;
    return module;
}
export function getImpersonationModule() {
    if (!singleton) {
        throw new Error('Impersonation module not installed. Call installImpersonation(auth) once at bootstrap.');
    }
    return singleton;
}
